import { sql } from "drizzle-orm";
import { boolean, date, decimal, index, integer, jsonb, pgTable, primaryKey, text, timestamp, uniqueIndex, varchar, type AnyPgColumn } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema, jsonSchema } from "drizzle-zod";
import { z } from "zod";
import { MONEY_STRING_PATTERN } from "./premium";
import { isValidPolicyDate, POLICY_DATE_FORMAT_MESSAGE } from "./policy-date";
import { ACORD_FORMS, type AcordFormId } from "./acord-forms";
import type { AorSource } from "./aor";
import { POLICY_SUBSTATUSES, type PolicySubstatus } from "./policy-substatus";

// Sentinel agency for system/unresolved records (e.g. webhook-time activity logs
// before agency resolution). Boot-seeded in server/index.ts via onConflictDoNothing,
// so it is guaranteed to exist in every environment. Anything that needs a fallback
// agency id MUST use this constant so the seed and the fallback can never drift.
export const UNASSIGNED_AGENCY_ID = "AGY-UNASSIGNED";

// Agencies table - represents trucking insurance agencies under LogRock
export const agencies = pgTable("agencies", {
  id: text("id").primaryKey(),
  name: text("name").notNull(),
  code: text("code").notNull().unique(),
  address: text("address"),
  phone: text("phone"),
  email: text("email"),
  authorizedRepresentative: text("authorized_representative"),
  status: text("status", { enum: ["active", "inactive", "suspended"] }).notNull().default("active"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  deletedAt: timestamp("deleted_at"),
});

// Session storage table (required for Replit Auth)
export const sessions = pgTable(
  "sessions",
  {
    sid: varchar("sid").primaryKey(),
    sess: jsonb("sess").notNull(),
    expire: timestamp("expire").notNull(),
  },
  (table) => [index("IDX_session_expire").on(table.expire)]
);

// Users table (Replit Auth compatible)
export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: varchar("email").unique(),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  profileImageUrl: varchar("profile_image_url"),
  crmAccess: boolean("crm_access").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Agents table - agents belong to an agency
export const agents = pgTable("agents", {
  id: text("id").primaryKey(),
  agencyId: text("agency_id").notNull().references(() => agencies.id),
  name: text("name").notNull(),
  email: text("email").notNull(),
  phone: text("phone").notNull(),
  status: text("status", { enum: ["active", "inactive", "pending"] }).notNull().default("pending"),
  policiesCount: integer("policies_count").notNull().default(0),
  totalPremium: decimal("total_premium", { precision: 12, scale: 2 }).notNull().default("0"),
  joinedDate: text("joined_date").notNull(),
  avatar: text("avatar").notNull(),
  performance: integer("performance").notNull().default(0),
  region: text("region").notNull(),
  licenseNumber: text("license_number"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// CSR-1527 (AMS-7 Phase B2a) — the AMS-only child tables (notes, documents,
// change-history, coi_issuances/notes/templates/verbiage, loss_payees +
// coverages/vehicles, waivers_of_subrogation + coverages, additional_insureds)
// keep their `policy_id` / `insured_id` columns but NO LONGER declare a FK
// constraint to `policies`/`insureds`. Rationale: DC is the source of truth for
// policies/insureds; once the local sync is removed (Phase B2b) a record created
// in DC after the freeze (fresh carrier scrape) never lands in these local
// tables, so an FK would block attaching AMS-owned data (a COI issuance, a note)
// to it. Dropping the constraints also unblocks the Phase C table drop. No live
// path relied on the ON DELETE CASCADE (policy/insured deletes are soft-deletes).
// Removing `.references()` makes `drizzle-kit push` (pre-deploy.sh) drop the
// constraints on deploy; the columns and all data are untouched.

// CSR-1872 (epic CSR-1870, Step 2) — the COI-owned tables (coi_requests,
// intercom_events, insured_coi_notes/templates/special_verbiage, coi_issuances)
// keep their agency_id / created_by / reviewed_by / updated_by / saved_by /
// issued_by / document_id columns but NO LONGER declare FK constraints to the
// AMS-owned `agencies` / `users` / `policy_documents` tables. These are now
// soft references (app-enforced), so the COI tables can move to the standalone
// COI service (Step 4) without dragging the AMS parents along. Same mechanism
// as the CSR-1527 block above: removing `.references()` makes `drizzle-kit
// push` (pre-deploy.sh) drop the constraints on deploy; the columns and all
// data are untouched. No live path relied on the constraints — agencies,
// users, and policy documents are soft-deleted (or never deleted), so the
// NO ACTION restrict never fires today.

// Policy documents table - compliance documents attached to policies
export const policyDocuments = pgTable("policy_documents", {
  id: text("id").primaryKey(),
  policyId: text("policy_id").notNull(),
  documentType: text("document_type").notNull(),
  name: text("name").notNull(),
  fileName: text("file_name").notNull(),
  fileSize: integer("file_size"),
  mimeType: text("mime_type"),
  objectPath: text("object_path").notNull(),
  uploadedBy: varchar("uploaded_by").references(() => users.id),
  notes: text("notes"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  deletedAt: timestamp("deleted_at"),
});

// Insured documents table (CSR-773) - documents attached to an insured (not a policy).
// Narrower than policy_documents: no documentType/notes columns. CSR-1257 adds an
// OPTIONAL policyId so an insured-level document can also be associated with one of
// the insured's policies. The document stays insured-scoped; the link is nullable and
// a blank link behaves exactly as before.
//
// CSR-2056 adds provenance for the "Generate ACORD Form" flow (CSR-1824): which
// form produced a row, and every policy it covers. `policyId` and `policyIds`
// coexist rather than one superseding the other — `resolveDocumentPolicyIds` in
// shared/insured-document-policies.ts is the single place that decides which one
// a given row's policies come from.
export const insuredDocuments = pgTable("insured_documents", {
  id: text("id").primaryKey(),
  insuredId: text("insured_id").notNull(),
  // CSR-1257 — optional link to one of the insured's policies. Nullable on purpose:
  // documents remain insured-scoped; this only records an additional policy association.
  policyId: text("policy_id"),
  // CSR-2056 — hand-uploaded vs generated by us. Defaulted so every row that
  // predates the column reads as 'upload' without a backfill sweep.
  source: text("source", { enum: ["upload", "acord_form"] })
    .notNull()
    .default("upload"),
  // CSR-2056 — which ACORD form produced this document, keyed to the CSR-2055
  // registry. Typed against `AcordFormId` so a row can only ever name a form the
  // registry actually defines. Null for uploads.
  acordFormId: text("acord_form_id").$type<AcordFormId>(),
  // CSR-2056 — every policy selected at generation time. No FK: policies live in
  // the Data Center now (CSR-1527) and these are DC-facing `dc-<n>` ids, resolved
  // to human-readable numbers through the DC at read time.
  policyIds: text("policy_ids").array(),
  name: text("name").notNull(),
  fileName: text("file_name").notNull(),
  fileSize: integer("file_size"),
  mimeType: text("mime_type"),
  objectPath: text("object_path").notNull(),
  uploadedBy: varchar("uploaded_by").references(() => users.id),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  deletedAt: timestamp("deleted_at"),
});

// Sync metadata - tracks when data was last synced from Data Center
export const syncMetadata = pgTable("sync_metadata", {
  id: text("id").primaryKey(),
  syncType: text("sync_type").notNull(),
  lastSyncedAt: timestamp("last_synced_at").notNull().defaultNow(),
  totalRecords: integer("total_records").notNull().default(0),
  status: text("status", { enum: ["success", "failed", "in_progress"] }).notNull().default("success"),
  errorMessage: text("error_message"),
  // Cursor for incremental delta sync — stores the (lastSync, id) high-water mark from DC
  lastSyncCursor: timestamp("last_sync_cursor"),
  lastSyncCursorId: integer("last_sync_cursor_id"),
});

// Activity logs for audit trail
export const activityLogs = pgTable("activity_logs", {
  id: text("id").primaryKey(),
  agencyId: text("agency_id").notNull().references(() => agencies.id),
  userId: varchar("user_id").references(() => users.id),
  action: text("action").notNull(), // e.g., "created", "updated", "deleted"
  entityType: text("entity_type").notNull(), // e.g., "agent", "policy", "agency"
  entityId: text("entity_id").notNull(),
  entityName: text("entity_name"),
  details: text("details"), // JSON string with additional details
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

// Policy change history - tracks field-level changes from syncs
export const policyChangeHistory = pgTable("policy_change_history", {
  id: text("id").primaryKey(),
  policyId: text("policy_id").notNull(),
  field: text("field").notNull(),
  oldValue: text("old_value"),
  newValue: text("new_value"),
  source: text("source").notNull(), // "sync", "manual", or "system" (CSR-1845 — AMS-applied guard flips)
  changedBy: varchar("changed_by").references(() => users.id),
  note: text("note"),
  changedAt: timestamp("changed_at").notNull().defaultNow(),
});

// Insured change history - tracks field-level changes from syncs and manual edits
export const insuredChangeHistory = pgTable("insured_change_history", {
  id: text("id").primaryKey(),
  insuredId: text("insured_id").notNull(),
  field: text("field").notNull(),
  oldValue: text("old_value"),
  newValue: text("new_value"),
  source: text("source").notNull(), // "sync" or "manual"
  changedBy: varchar("changed_by").references(() => users.id),
  note: text("note"),
  changedAt: timestamp("changed_at").notNull().defaultNow(),
});

// CSR-771 — revised notes table. See migrations/0009_revise_notes_for_csr_771.sql.
// 13 columns supporting cross-surface (insured + policy) linkage, AMS vs.
// NowCerts source discrimination, edit tracking, and soft delete. The DB-level
// CHECK (source IN ('ams','nowcerts')) is enforced via the migration only —
// drizzle's check helper isn't loaded in this repo, but the `text(... { enum })`
// declaration here gives the type-derivation the same domain.
export const notes = pgTable(
  "notes",
  {
    id: text("id").primaryKey(),
    insuredId: text("insured_id").notNull(),
    policyId: text("policy_id"),
    title: text("title").notNull(),
    details: text("details"),
    source: text("source", { enum: ["ams", "nowcerts"] }).notNull(),
    authorUserId: varchar("author_user_id").references(() => users.id),
    authorDisplayName: text("author_display_name"),
    nowcertsExternalId: text("nowcerts_external_id"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
    isEdited: boolean("is_edited").notNull().default(false),
    deletedAt: timestamp("deleted_at"),
    // CSR-1885 — global pin. null = not pinned; set = pinned-at time (also the
    // sort key among pinned notes, most-recent first). Pinnable on all sources.
    pinnedAt: timestamp("pinned_at"),
  },
  (table) => [
    // CSR-771 — declared here so `db:push` doesn't try to drop them.
    // The migration creates the same three indexes via raw SQL.
    index("idx_notes_insured_active").on(
      table.insuredId,
      table.deletedAt,
      table.createdAt,
    ),
    index("idx_notes_policy_active").on(
      table.policyId,
      table.deletedAt,
      table.createdAt,
    ),
    // CSR-1885 — keeps the pinned-first read cheap.
    index("idx_notes_insured_pinned").on(
      table.insuredId,
      table.deletedAt,
      table.pinnedAt,
    ),
    uniqueIndex("uq_notes_nowcerts_external")
      .on(table.nowcertsExternalId)
      .where(sql`source = 'nowcerts' AND nowcerts_external_id IS NOT NULL`),
  ],
);

// Policy statuses that count as "in force" for premium calculations and filters.
// This is an allow-list: any status not listed here — including "cancelled" and,
// per CSR-1726, "flat cancel" — is excluded by omission. Do not add them here.
export const IN_FORCE_STATUSES = ["active", "pending cancel", "renewing", "reinstated", "non-renewal"] as const;

// CSR-1987 — the cancelled-family statuses in which a record-update cancellation
// legitimately leaves a NEGATIVE fee (`existing − entered`, which need not push
// the premium itself negative — a partial fee withdrawal can leave premium
// positive or zero). Only these accept signed fees on the DC read; every other
// status keeps the CSR-1983/CSR-1630 unsigned guard, where a negative
// carrier-sync fee is garbled data and drops the whole breakdown.
const CANCELLATION_STATUSES = new Set([
  "cancelled",
  "pending cancel",
  "flat cancel",
]);

export function isCancellationStatus(status: string | null | undefined): boolean {
  return status != null && CANCELLATION_STATUSES.has(status);
}


export const TRACKED_INSURED_FIELDS = [
  "name", "commercialName", "dbaName", "dotNumber", "mcNumber",
  "email", "telephone", "fax",
  "phyStreet", "phyCity", "phyState", "phyZip", "phyCountry",
  "mailingStreet", "mailingCity", "mailingState", "mailingZip", "mailingCountry",
  "businessOrg", "companyOfficer", "radius",
  "totalDrivers", "totalPowerUnits", "totalFleetSize",
  "salesAgent", "retentionAgent",
] as const;

export const TRACKED_POLICY_FIELDS = [
  "policyNumber", "customerName", "carrier", "parentCarrier",
  "status", "producer", "type", "effectiveDate", "expirationDate",
  "premium", "dotNumber", "agency", "agencyId", "companyEmail",
  "carrierNaic",
  // CSR-1604: premium breakdown components (premium itself is the tracked total).
  "policyPremium", "brokerFee", "policyFees", "taxes",
  // CSR-748 / CSR-746: manual-edit fields surfaced in the policy change-history.
  "mga", "financeCompany", "paymentPlan", "boundDate", "cancellationDate",
  "dbaName",
  // CSR-2336 — the DC-owned substatus qualifying `status` (e.g. "non-renewal").
  "substatus",
] as const;

// CSR-2197 (PB7.3) — `certificate_holders` and `insured_additional_insureds`
// were declared here until the COI service became their sole owner (CSR-2142 /
// CSR-2023 seams; AMS copies frozen since 2026-08-13, verified contained by
// identity+id cross-check on 2026-08-24). Read shapes and the holder body
// validators live on in `shared/coi-service-types.ts`.

// Custom carriers added via "Other" option in the policy form
export const carriers = pgTable(
  "carriers",
  {
    id: text("id").primaryKey(),
    name: text("name").notNull().unique(),
    naic: text("naic"),
    parentCarrier: text("parent_carrier"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("uq_carriers_lower_naic")
      .on(sql`LOWER(${table.naic})`)
      .where(sql`${table.naic} IS NOT NULL`),
  ],
);

// CSR-588 — master registry of Insurance Companies surfaced on the Companies
// page. Three categories share one table: Parent Company, Insurance Carrier,
// and MGA. Parent Companies group carriers/MGAs via parent_company_id; they
// never carry commission rates or a parent of their own (enforced at the
// route layer in CSR-818). v1 links policies to insurance companies via
// case-insensitive name match on policies.carrier / parent_carrier / mga
// (decision A from CSR-588 planning); FK promotion is deferred to CSR-797.
// Uniqueness is scoped to (LOWER(name), category) — CSR-1669/CSR-1674 — so
// the same brand name may exist once per category (e.g. "Cover Whale" as
// both a Parent Company and an MGA), while a second same-name row within the
// same category is still rejected. Non-partial: soft-deleted rows still
// collide within their own category (CSR-1652 semantics).
export const insuranceCompanies = pgTable(
  "insurance_companies",
  {
    id: text("id").primaryKey(),
    name: text("name").notNull(),
    category: text("category", {
      enum: ["parent_company", "insurance_carrier", "mga"],
    }).notNull(),
    parentCompanyId: text("parent_company_id").references(
      (): AnyPgColumn => insuranceCompanies.id,
      { onDelete: "set null" },
    ),
    naic: text("naic"),
    website: text("website"),
    street: text("street"),
    city: text("city"),
    state: text("state"),
    zip: text("zip"),
    // Stored as percentage (e.g. 12.50 means 12.5%). Must be null for
    // parent_company rows -- enforced at the route layer in CSR-818.
    newCommissionPct: decimal("new_commission_pct", { precision: 5, scale: 2 }),
    renewalCommissionPct: decimal("renewal_commission_pct", { precision: 5, scale: 2 }),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
    deletedAt: timestamp("deleted_at"),
  },
  (table) => [
    uniqueIndex("uq_insurance_companies_lower_name_category").on(
      sql`LOWER(${table.name})`,
      table.category,
    ),
    index("idx_insurance_companies_category").on(table.category),
    index("idx_insurance_companies_parent").on(table.parentCompanyId),
  ],
);

// CSR-588 — contacts attached to an insurance company (carrier / MGA / parent).
// Hard-delete on company cascade. Only one contact per company may carry
// is_primary = true; demotion is enforced at the route layer in CSR-820.
export const insuranceCompanyContacts = pgTable(
  "insurance_company_contacts",
  {
    id: text("id").primaryKey(),
    insuranceCompanyId: text("insurance_company_id")
      .notNull()
      .references(() => insuranceCompanies.id, { onDelete: "cascade" }),
    fullName: text("full_name").notNull(),
    email: text("email"),
    phone: text("phone"),
    role: text("role"),
    isPrimary: boolean("is_primary").notNull().default(false),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [
    index("idx_insurance_company_contacts_company").on(table.insuranceCompanyId),
  ],
);

// CSR-1606 — per-carrier commission overrides by line of business. A carrier
// keeps its general rates on `insurance_companies` (new/renewal); rows here
// override the general rate for policies of a specific line of business. The
// matching rate is applied (as a pre-fill only) when a policy is created.
export const insuranceCompanyCommissionRates = pgTable(
  "insurance_company_commission_rates",
  {
    id: text("id").primaryKey(),
    insuranceCompanyId: text("insurance_company_id")
      .notNull()
      .references(() => insuranceCompanies.id, { onDelete: "cascade" }),
    lineOfBusiness: text("line_of_business").notNull(),
    ratePct: decimal("rate_pct", { precision: 5, scale: 2 }).notNull(),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [
    index("idx_ic_commission_rates_company").on(table.insuranceCompanyId),
    uniqueIndex("uq_ic_commission_rates_company_lob").on(
      table.insuranceCompanyId,
      table.lineOfBusiness,
    ),
  ],
);

// System-wide settings (single row).
// CSR-837: legacy COI `feature_*` boolean columns were moved to the
// admin-managed `feature_flags` registry (see below). Reads now go
// through `services/featureFlags.ts:isFeatureFlagActive`.
export const systemSettings = pgTable("system_settings", {
  id: text("id").primaryKey().default("system"),
  syncIntervalMinutes: integer("sync_interval_minutes").notNull().default(15),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// CSR-2196 (PB7.2) — `inbound_emails`, `intercom_events`, `coi_requests`,
// `coi_exclusion_phrases` and `coi_issuances` were declared here until the COI
// service became the sole owner of COI history. AMS last wrote one of them on
// 2026-08-07 and now reads all of it over HTTP; PB7.1 verified the service holds
// every row. Their read shapes live on in `shared/coi-service-types.ts`.

// Admin-managed feature flag registry. CSR-837: the canonical home for
// all feature flags, including the COI gates that previously lived as
// boolean columns on `system_settings`. Use `isFeatureFlagActive(name)`
// from server/services/featureFlags.ts to read.
export const featureFlags = pgTable(
  "feature_flags",
  {
    id: text("id").primaryKey(),
    name: text("name").notNull().unique(),
    isActive: boolean("is_active").notNull().default(false),
    description: text("description"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [
    index("idx_feature_flags_name").on(table.name),
    index("idx_feature_flags_active")
      .on(table.isActive)
      .where(sql`${table.isActive} = true`),
  ],
);

// Insured COI notes - per-insured rules and notes for certificate handling (separate from read-only insureds table)
export const insuredCoiNotes = pgTable("insured_coi_notes", {
  id: text("id").primaryKey(),
  insuredId: text("insured_id").notNull().unique(),
  coiNotes: jsonb("coi_notes").$type<CoiNote[]>().default([]),
  updatedBy: varchar("updated_by"),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export interface CoiNote {
  id: string;
  type: "info" | "rule";
  content: string;
  source?: "manual" | "nowcerts_import";
  nowcertsExternalId?: string;
}

// CSR-1117 — Saved COI template per insured. One row per insured (unique
// insured_id), storing the full CoiTemplateData blob the editor edits. The
// `savedAt` timestamp is the single source of truth for the 1-hour send-time
// sync-protection window. Mirrors insuredCoiNotes (a per-insured JSONB side
// table) rather than adding sync-churned columns to `insureds`. Typed as a
// generic record here to avoid a server→shared import cycle; the route/worker
// cast to CoiTemplateData.
export const insuredCoiTemplates = pgTable("insured_coi_templates", {
  id: text("id").primaryKey(),
  insuredId: text("insured_id").notNull().unique(),
  templateData: jsonb("template_data").$type<Record<string, unknown>>(),
  savedBy: varchar("saved_by"),
  savedAt: timestamp("saved_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// CSR-1627 — durable per-insured COI "Description of Operations / Remarks"
// special verbiage. This text is manual-input-only (no carrier/policy source),
// so unlike `insured_coi_templates` it must SURVIVE every sync. A manual sync
// still clears the template snapshot (so coverages refresh), but first carves
// the verbiage into this table; the rebuild paths re-seed it from here. One row
// per insured (unique insured_id). Mirrors the per-insured COI side-table
// pattern (insured_coi_notes / insured_coi_templates) — never on the
// sync-churned `insureds` table.
export const insuredCoiSpecialVerbiage = pgTable("insured_coi_special_verbiage", {
  id: text("id").primaryKey(),
  insuredId: text("insured_id").notNull().unique(),
  verbiage: text("verbiage"),
  updatedBy: varchar("updated_by"),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// CSR-1224 — whole-insured refresh batch telemetry. Ephemeral, best-effort
// (like the pg-boss poll rows): NO FK to policies/insureds — a child can end
// `removed` after a soft-delete, and telemetry must never fail or block a
// refresh on a constraint. One batch row per insured Refresh click that
// enqueues >=1 poll job; one item row per enqueued child. "Done" =
// count(items) == total; `completedAt` is flipped by a row-locked compare-and-set
// so the ams.insured_refresh.* metrics emit exactly once. `carrier` is the
// single shared parent carrier across the batch, else "mixed".
export const policySyncBatches = pgTable("policy_sync_batches", {
  id: text("id").primaryKey(), // generated batchId, e.g. "BATCH-<uuid>"
  insuredId: text("insured_id").notNull(),
  insuredName: text("insured_name"),
  carrier: text("carrier").notNull(),
  total: integer("total").notNull(),
  triggeredAt: timestamp("triggered_at").notNull().defaultNow(),
  completedAt: timestamp("completed_at"),
});

// One row per enqueued child poll job in a batch. Composite PK (batchId,
// policyId) is the idempotency key: each child upserts its terminal status, so a
// pg-boss retry / the 404-rethrow path can't double-count.
export const policySyncBatchItems = pgTable(
  "policy_sync_batch_items",
  {
    batchId: text("batch_id").notNull().references(() => policySyncBatches.id, { onDelete: "cascade" }),
    policyId: text("policy_id").notNull(),
    status: text("status").notNull(), // completed | removed | failed | timeout
    recordedAt: timestamp("recorded_at").notNull().defaultNow(),
  },
  (table) => [primaryKey({ columns: [table.batchId, table.policyId] })],
);


// CSR-792 — Loss Payees: insured-scoped, separate from certificate_holders.
// Stores its own contact data (no FK to certificate_holders) because LPs are
// lienholders/lenders that exist at the policy/insured level. Required-field
// columns are NOT NULL — future DC-extraction inserts use empty strings for
// placeholders; the auto-clear branch checks `<> ''` for completeness.
// `pending_additional_info` is the single source of truth for the orange
// "Pending Additional Information" tag (stored, NOT derived).
export const lossPayees = pgTable(
  "loss_payees",
  {
    id: text("id").primaryKey(),
    insuredId: text("insured_id").notNull(),
    name: text("name").notNull(),
    email: text("email").notNull(),
    street: text("street").notNull(),
    city: text("city").notNull(),
    state: text("state").notNull(),
    zip: text("zip").notNull(),
    specialVerbiage: text("special_verbiage"),
    pendingAdditionalInfo: boolean("pending_additional_info").notNull().default(false),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("uq_loss_payees_per_insured").on(
      table.insuredId,
      sql`LOWER(TRIM(${table.name}))`,
      sql`LOWER(TRIM(${table.email}))`,
    ),
    index("idx_loss_payees_insured_id").on(table.insuredId),
  ],
);

// CSR-792 — link table: loss payee ↔ specific coverage on a specific policy.
// `coverage_key` matches `buildCoverageLines(policy.coverages).key`
// (e.g. `generalLiability`, `autoLiability`, `cargo`, `physicalDamage`).
export const lossPayeeCoverages = pgTable(
  "loss_payee_coverages",
  {
    id: text("id").primaryKey(),
    lossPayeeId: text("loss_payee_id").notNull().references(() => lossPayees.id, { onDelete: "cascade" }),
    policyId: text("policy_id").notNull(),
    coverageKey: text("coverage_key").notNull(),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("uq_loss_payee_coverages").on(
      table.lossPayeeId,
      table.policyId,
      table.coverageKey,
    ),
    index("idx_loss_payee_coverages_lp").on(table.lossPayeeId),
  ],
);

// CSR-792 / CSR-1855 — link table: loss payee ↔ asset on a specific policy.
// Keyed on `(policy_id, asset_key)`, where `asset_key` mirrors the sync layer's
// `keyForVehicle` precedence (databaseId → VIN → content hash; see
// `@shared/asset-identity`). This lets VIN-less trailers/vehicles be linked —
// VIN is not required on an asset. The read path re-resolves the full
// PolicyVehicle from `policies.vehicles`/`trailers` JSONB by matching any of the
// asset's identity forms, so the picker always shows the latest data.
// `asset_key` is NULLABLE at the DB level (app-enforced non-null): `drizzle-kit
// push` runs before the 0036 backfill, so a NOT NULL column with no default
// would fail ADD COLUMN on populated tables and a constant default would collide
// on the unique index. `vin` is kept (now nullable) for the backfill anchor and
// debugging; it is no longer the resolution key.
export const lossPayeeVehicles = pgTable(
  "loss_payee_vehicles",
  {
    id: text("id").primaryKey(),
    lossPayeeId: text("loss_payee_id").notNull().references(() => lossPayees.id, { onDelete: "cascade" }),
    policyId: text("policy_id").notNull(),
    assetKey: text("asset_key"),
    vin: text("vin"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("uq_loss_payee_vehicles").on(
      table.lossPayeeId,
      table.policyId,
      table.assetKey,
    ),
    index("idx_loss_payee_vehicles_lp").on(table.lossPayeeId),
  ],
);

// CSR-1101 — Waivers of Subrogation: insured-scoped, mirrors loss_payees
// structurally. Stores its own contact data (no FK to certificate_holders).
// Two deliberate divergences from loss_payees: `email` is NULLABLE (WoS does
// not require an email), and the per-insured unique index is name-only
// (insured_id, LOWER(TRIM(name))) — NOT name+email — because email is
// nullable and Postgres treats NULLs as distinct, so two same-name rows with
// different/empty emails must still be rejected as duplicates.
// `pending_additional_info` is the single source of truth for the orange
// "Pending Additional Information" tag (stored, NOT derived).
export const waiversOfSubrogation = pgTable(
  "waivers_of_subrogation",
  {
    id: text("id").primaryKey(),
    insuredId: text("insured_id").notNull(),
    name: text("name").notNull(),
    email: text("email"),
    street: text("street").notNull(),
    city: text("city").notNull(),
    state: text("state").notNull(),
    zip: text("zip").notNull(),
    specialVerbiage: text("special_verbiage"),
    pendingAdditionalInfo: boolean("pending_additional_info").notNull().default(false),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("uq_waivers_of_subrogation_per_insured").on(
      table.insuredId,
      sql`LOWER(TRIM(${table.name}))`,
    ),
    index("idx_waivers_of_subrogation_insured_id").on(table.insuredId),
  ],
);

// CSR-1101 — link table: waiver of subrogation ↔ specific coverage on a
// specific policy. `coverage_key` matches `buildCoverageLines(policy.coverages)
// .key` (e.g. `generalLiability`, `autoLiability`, `umbrella`, `workersComp`).
// No vehicles link table — WoS has no vehicle-level granularity (OQ-10).
export const waiverOfSubrogationCoverages = pgTable(
  "waiver_of_subrogation_coverages",
  {
    id: text("id").primaryKey(),
    waiverOfSubrogationId: text("waiver_of_subrogation_id").notNull().references(() => waiversOfSubrogation.id, { onDelete: "cascade" }),
    policyId: text("policy_id").notNull(),
    coverageKey: text("coverage_key").notNull(),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("uq_waiver_of_subrogation_coverages").on(
      table.waiverOfSubrogationId,
      table.policyId,
      table.coverageKey,
    ),
    index("idx_waiver_of_subrogation_coverages_wos").on(table.waiverOfSubrogationId),
  ],
);

// CSR-1658 — Commodities. A global catalog of commodity names shared across all
// insureds (search suggests any commodity ever added anywhere). Append-only: a
// row is never deleted when an insured unlinks it. `LOWER(TRIM(name))` uniqueness
// prevents "Autos" / "autos" duplicates. Mirrors the loss_payees text-PK style.
export const commodities = pgTable(
  "commodities",
  {
    id: text("id").primaryKey(),
    name: text("name").notNull(),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("uq_commodities_name").on(sql`LOWER(TRIM(${table.name}))`),
  ],
);

// CSR-1658 — link table: insured ↔ commodity. insuredId is a soft link (mirrors
// loss_payees.insured_id — insureds are DC-sourced, no local FK target).
// commodityId is a hard FK: both sides are AMS-local. Deleting a link never
// touches the catalog row.
export const insuredCommodities = pgTable(
  "insured_commodities",
  {
    id: text("id").primaryKey(),
    insuredId: text("insured_id").notNull(),
    commodityId: text("commodity_id")
      .notNull()
      .references(() => commodities.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("uq_insured_commodities").on(table.insuredId, table.commodityId),
    index("idx_insured_commodities_insured_id").on(table.insuredId),
    index("idx_insured_commodities_commodity_id").on(table.commodityId),
  ],
);


// Default (hardcoded) carrier names — always available in the carrier dropdown
export const DEFAULT_CARRIERS = [
  "Progressive",
  "Geico",
  "CoverWhale",
  "National",
  "Zurich",
  "Great West",
  "Canal Insurance",
  "OOIDA",
  "Sentry",
  "Northland",
];

// Parent carrier groupings — maps carrier names to their parent group.
// Carriers not listed here are standalone (parentCarrier = null).
export const CARRIER_PARENT_MAP: Record<string, string> = {
  "Progressive": "Progressive",
  "Geico": "Geico",
};

// Allow-list: any status not listed here — including "cancelled" and, per
// CSR-1726, "flat cancel" — is excluded from COI eligibility by omission. Do not add them here.
export const COI_ELIGIBLE_STATUSES = new Set(["active", "pending cancel", "renewing", "reinstated", "non-renewal"]);
// CSR-2286/CSR-2250 (mirrors logrock-coi CSR-2289) — the producer-facing policy
// picker selects by a narrower rule than the automation path: the in-force set
// below, plus a Renewal Offer whose term has not started yet.
// COI_ELIGIBLE_STATUSES is left untouched.
export const COI_PICKER_IN_FORCE_STATUSES = new Set(["active", "pending cancel", "renewing"]);
export const COI_RENEWAL_OFFER_STATUS = "renewal offer";

export function isCoiDefaultSelectedPolicy(policy: { status?: string | null }): boolean {
  return COI_PICKER_IN_FORCE_STATUSES.has((policy.status ?? "").trim().toLowerCase());
}

// CSR-2286 — is this selection the one the picker opens with, i.e. every
// in-force term of the insured and nothing else? Compared as SETS over the
// insured's FULL policy list, so an id the client left out disqualifies the
// selection exactly as an extra id does. Recomputed server-side on every
// request rather than trusted from the client, because this is what decides
// whether the saved per-insured template still describes the certificate.
// CSR-2287 — it lives here rather than in coiTemplate.ts so the picker's
// "(default)" copy cannot drift from the rule that picks the saved template.
export function isDefaultCoiPolicySelection(
  allPolicies: { id: string; status?: string | null }[],
  selectedPolicyIds: string[],
): boolean {
  const selected = new Set(selectedPolicyIds);
  const defaults = new Set(
    allPolicies.filter((p) => isCoiDefaultSelectedPolicy(p)).map((p) => p.id),
  );
  if (defaults.size !== selected.size) return false;
  for (const id of Array.from(defaults)) {
    if (!selected.has(id)) return false;
  }
  return true;
}

// Parses both stored term forms to calendar components instead of through
// `new Date(str)` — an ISO string parses as UTC midnight and MM/DD/YYYY as
// local midnight, so a timestamp comparison shifts by a day per server offset.
function coiCalendarDay(value: string | null | undefined): number | null {
  const raw = (value ?? "").trim();
  if (!raw) return null;
  // Anything after the date must be a real time (`T`/space then a digit), so a
  // date with malformed text glued to it is rejected rather than half-read.
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ]\d.*)?$/.exec(raw);
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(raw);
  let year: number, month: number, day: number;
  if (iso) {
    [year, month, day] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
  } else if (us) {
    [year, month, day] = [Number(us[3]), Number(us[1]), Number(us[2])];
  } else {
    return null;
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  // A range check alone accepts 2027-02-31, which a certificate would then read
  // as a future term. Round-trip through a real calendar date instead: an
  // impossible day rolls forward (Feb 31 → Mar 3) and fails to come back.
  const calendar = new Date(Date.UTC(year, month - 1, day));
  if (
    calendar.getUTCFullYear() !== year ||
    calendar.getUTCMonth() !== month - 1 ||
    calendar.getUTCDate() !== day
  ) {
    return null;
  }
  return year * 10000 + month * 100 + day;
}

// CSR-2299 — one authoritative business day for every runtime: the picker runs
// in the producer's browser and the preview guard on a server in another zone,
// so the machine's own zone must not decide whether a term has started.
export const COI_BUSINESS_TIME_ZONE = "America/New_York";

const coiBusinessDayFormat = new Intl.DateTimeFormat("en-CA", {
  timeZone: COI_BUSINESS_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export function coiBusinessCalendarDay(instant: Date = new Date()): number | null {
  return coiCalendarDay(coiBusinessDayFormat.format(instant));
}

// CSR-2286 — a past-effective Renewal Offer was never bound, so it is not
// coverage and may not be put on a certificate. Compared date-only.
// CSR-2298 — the effective date itself is still open: the offer can be bound
// through midnight of that day, so only a strictly earlier date is past.
export function isCoiSelectablePolicy(
  policy: { status?: string | null; effectiveDate?: string | null },
  today: Date = new Date(),
): boolean {
  if (isCoiDefaultSelectedPolicy(policy)) return true;
  if ((policy.status ?? "").trim().toLowerCase() !== COI_RENEWAL_OFFER_STATUS) return false;
  const effective = coiCalendarDay(policy.effectiveDate);
  if (effective === null) return false;
  const current = coiBusinessCalendarDay(today);
  if (current === null) return false;
  return effective >= current;
}

// CSR-1349: closed set of send origins persisted on `coiIssuances.sendOrigin`.
// Request source "email" is intentionally excluded — it is not part of the
// send-time monitoring buckets (nor of "combined").
export const COI_SEND_ORIGINS = ["manual", "coi_form", "intercom_webhook"] as const;
export type CoiSendOrigin = (typeof COI_SEND_ORIGINS)[number];

// CSR-751: closed set of "Additional Information" categories the Send COI
// modal exposes. Persisted on `coiIssuances.additionalInfo` and rendered as
// tags on the insured page's COI list. Keep in lockstep with the toggle list
// in `client/src/components/insured/SendCOIDialog.tsx`.
export const COI_ADDITIONAL_INFO_OPTIONS = [
  "Vehicles",
  "Trailers",
  "Drivers",
  "Additional Insured",
  "Loss Payee",
  "Special Verbiage",
] as const;
export type CoiAdditionalInfoOption = (typeof COI_ADDITIONAL_INFO_OPTIONS)[number];

// Sanitize caller-supplied additionalInfo: keep only recognized options and
// drop duplicates. Returns null when the input is missing or empty so the
// DB column stays null for legacy/external callers (intercom worker, etc.).
export function sanitizeCoiAdditionalInfo(input: unknown): CoiAdditionalInfoOption[] | null {
  if (!Array.isArray(input)) return null;
  const allowed = new Set<string>(COI_ADDITIONAL_INFO_OPTIONS);
  const seen = new Set<string>();
  const out: CoiAdditionalInfoOption[] = [];
  for (const value of input) {
    if (typeof value !== "string") continue;
    if (!allowed.has(value)) continue;
    if (seen.has(value)) continue;
    seen.add(value);
    out.push(value as CoiAdditionalInfoOption);
  }
  return out.length > 0 ? out : null;
}

export const CARRIER_SYNC_SUPPORTED: string[] = ["Progressive", "Geico"];

export type CarrierSyncFamily = "progressive" | "geico";

export function matchesCarrierFamily(
  carrier: string,
  parentCarrier: string | null | undefined,
  family: string,
): boolean {
  const familyLower = family.toLowerCase();
  return [carrier, parentCarrier].some((name) =>
    (name || "").toLowerCase().includes(familyLower),
  );
}

/** The DC-accepted sync token ("progressive" | "geico") for this policy's
 *  carrier/parentCarrier, or null when neither names a supported family. This
 *  is the single source of truth for both eligibility and the value sent to
 *  DC's `POST /api/external/sync` — DC accepts only these exact strings, so
 *  eligibility and the trigger payload must never be decided separately
 *  (CSR-2281). */
export function getCarrierSyncFamily(
  carrier: string | null | undefined,
  parentCarrier?: string | null,
): CarrierSyncFamily | null {
  const family = CARRIER_SYNC_SUPPORTED.find((candidate) =>
    matchesCarrierFamily(carrier ?? "", parentCarrier, candidate),
  );
  return family ? (family.toLowerCase() as CarrierSyncFamily) : null;
}

export function isCarrierSyncSupported(carrier: string, parentCarrier?: string | null): boolean {
  return getCarrierSyncFamily(carrier, parentCarrier) !== null;
}

// Safety data interfaces for insured JSONB columns
export interface InspectionSummary {
  totalInspections: number;
  vehicleInspections: number;
  driverInspections: number;
  outOfServiceRate: number;
  vehicleOosRate: number;
  driverOosRate: number;
  timeframeDays: number;
}

export interface CrashSummary {
  totalCrashes: number;
  fatal: number;
  injury: number;
  towaway: number;
  timeframeDays: number;
}

export interface Violation {
  code: string;
  description: string;
  category: string;
  severity: number;
  count: number;
  oos: boolean;
}

export interface BasicScores {
  unsafeDriving: number | null;
  hoursOfService: number | null;
  vehicleMaintenance: number | null;
  controlledSubstances: number | null;
  hazardousMaterials: number | null;
  driverFitness: number | null;
  crashIndicator: number | null;
  asOfDate: string;
}

// Standard truck/vehicle types used in trucking insurance
export const TRUCK_TYPES = [
  "Tractor",
  "Straight Truck",
  "Trailer",
  "Pickup",
  "Van",
  "Bus",
  "Flatbed",
  "Tanker",
  "Dump Truck",
  "Reefer",
  "Car Carrier",
  "Intermodal Chassis",
] as const;

export type TruckType = (typeof TRUCK_TYPES)[number];

// Vehicle interface — shared between server and client.
// `addedAt` / `removedAt` are stamped by the sync merge (server/asset-merge.ts):
// `removedAt` is null while the asset is present on the carrier feed; set to an
// ISO timestamp when the carrier removes it. A re-addition flips it back to null
// and refreshes `addedAt`.
// CSR-789: stated value + per-vehicle deductibles populated upstream by the
// data-center Progressive sync (see logrock-dc CSR-757). Optional because legacy
// rows that were last synced before that work landed will not carry them.
export interface PolicyVehicle {
  vin?: string;
  year?: number | string;
  make?: string;
  model?: string;
  truckType?: string;
  value?: string;
  deductibleComp?: string;
  deductibleCollision?: string;
  // CSR-1659 — operating radius. Free-text (not numeric) so CS can record
  // values like "Unlimited" or "150 mi". AMS-entered manual annotation stored in
  // the vehicles/trailers JSONB and round-tripped through DC verbatim, like
  // value/deductibleComp above.
  radius?: string;
  databaseId?: string;
  addedAt?: string;
  removedAt?: string | null;
}

export type PolicyTrailer = PolicyVehicle;

// CSR-1144 — AMS-only Physical Damage vehicle selection, persisted in the
// sync-exempt `policies.physical_damage_vehicles` column (see the column
// comment for why it cannot live inside `coverages`). VIN is the stable key
// (matches the COI vehicle-dedup convention); vehicles without a VIN are not
// individually selectable. `null` on the policy means "never curated" — the
// PD card then falls back to deductible inference. An object (even with empty
// arrays) means the selection is explicit/curated.
export interface PhysicalDamageVehicleSelection {
  vehicleVins: string[];
  trailerVins: string[];
}

// Carrier-imported Additional Insured (CSR-743). Populated by the LogRock
// Data Center Progressive sync from either the inline tabular block (Layout A)
// on the policy-detail page or the paginated "Policy Details" sidebar table
// (Layout B). Address fields are optional because legacy rows synced before
// CSR-743 carry only `name`, and a small number of carrier-side rows still
// arrive name-only when navigation/extraction can't reach the address page.
export interface PolicyAdditionalInsured {
  name: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  // CSR-1593 — per-coverage NowCerts Additional-Insured flags surfaced by the
  // Data Center (raw NC `automobileLiability` / `otherLiability` /
  // `otherLiability2` / `generalLiability` / `umbrellaLiability` / `cargo`).
  // Only emitted by DC when true; consumed by the daily reconciliation to fill
  // `insured_additional_insureds.coverageIds`. See server/services/ai-coverage-mapping.ts.
  ncAutomobileLiability?: boolean;
  ncOtherLiability?: boolean;
  ncOtherLiability2?: boolean;
  ncGeneralLiability?: boolean;
  ncUmbrellaLiability?: boolean;
  ncCargo?: boolean;
}

// Carrier-imported Waiver of Subrogation (CSR-1456). Same wire shape as
// PolicyAdditionalInsured — names come from the Progressive policy-detail page
// and are upgraded to name+address from the Policy Details sidebar (CSR-1102,
// DC). Address fields are optional: a name-only row arrives when the sidebar
// address page can't be reached. Stored as the `policies.waivers_of_subrogation`
// JSONB snapshot and materialized at sync time into insured-scoped
// `waivers_of_subrogation` records. The separate "Blanket Waiver" Yes/No flag is
// the `blanketWaiverOfSubrogation` policy column, not part of this array.
export interface PolicyWaiverOfSubrogation {
  name: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}

// Carrier-imported Loss Payee (CSR-1363). Same wire shape as
// PolicyAdditionalInsured / PolicyWaiverOfSubrogation — names arrive from the
// Data Center's NowCerts-sourced policy row. Address fields are optional: a
// name-only row arrives when the source carries no address. The extra
// `type`/`status`/`source` fields are tolerated on the wire and ignored by the
// materializer. Stored as the `policies.loss_payees` JSONB snapshot and
// materialized at sync time into insured-scoped `loss_payees` records. NowCerts
// loss payees carry no email, so materialized rows use an empty-string email
// placeholder (the `loss_payees.email` column is NOT NULL).
export interface PolicyLossPayee {
  name: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  type?: string;
  status?: string;
  source?: string;
}

// Driver interface — shared between server and client.
// Canonical fields (`name`, `dateOfBirth`) are normalized at sync time
// (see `normalizeDrivers` in server/sync.ts). The legacy DC passthrough
// fields (`firstName`, `lastName`, `birthday`) are kept optional so we can
// read pre-normalization rows without losing the granular data.
// `addedAt` / `removedAt` follow the same semantics as `PolicyVehicle`.
export interface PolicyDriver {
  name?: string;
  firstName?: string;
  lastName?: string;
  licenseNumber?: string;
  licenseState?: string;
  dateOfBirth?: string;
  birthday?: string;
  dateOfHire?: string;
  yearsExperience?: number | string;
  databaseId?: string;
  addedAt?: string;
  removedAt?: string | null;
}

// CSR-1404 — explicit, user-intended Active/Removed change for a single asset,
// sent alongside an Edit-modal save. Applied to `removedAt` on the matching
// vehicles/trailers/drivers JSONB entries after the sync merge (the merge itself
// preserves `removedAt`, so this is the only path that can reactivate an asset).
// AMS-only: never forwarded to the Data Center.
export const assetStatusChangeSchema = z.object({
  arrayKey: z.enum(["vehicles", "trailers", "drivers"]),
  match: z
    .object({
      databaseId: z.string().optional(),
      vin: z.string().optional(),
      addedAt: z.string().optional(),
      name: z.string().optional(),
    })
    .refine((m) => Boolean(m.databaseId || m.vin || m.addedAt || m.name), {
      message: "asset status change requires at least one identity field",
    }),
  removed: z.boolean(),
});

export type AssetStatusChange = z.infer<typeof assetStatusChangeSchema>;

// Per-coverage lifecycle status, surfaced from the Data Center sync (CSR-679).
// `removed` means the coverage was active in a prior sync and is no longer on
// the carrier policy — AMS keeps the row so users can see what used to be on
// the policy, but excludes it from COIs. Missing → treat as `active`.
export type CoverageStatus = "active" | "removed";

// Policy coverages interface — shared between server and client
export interface PolicyCoverages {
  generalLiability?: {
    status?: CoverageStatus;
    eachOccurrence: string;
    damageToRented: string;
    medExp: string;
    personalAdvInjury: string;
    generalAggregate: string;
    productsCompOpAgg: string;
    claimsMade: boolean;
    aggregateAppliesPer: 'policy' | 'project' | 'loc';
  };
  autoLiability?: {
    status?: CoverageStatus;
    combinedSingleLimit: string;
    bodilyInjuryPerPerson: string;
    bodilyInjuryPerAccident: string;
    propertyDamage: string;
    autoType: 'any' | 'all_owned' | 'scheduled' | 'hired' | 'non_owned';
    uninsuredMotoristCsl?: string;
    uninsuredMotoristPd?: string;
    underinsuredMotoristBi?: string;
    underinsuredMotoristType?: string;
    medicalPayments?: string;
    basicPip?: string;
    pip?: string;
    umStacking?: boolean | null;
    uimStacking?: boolean | null;
    umpdStacked?: boolean | null;
  };
  nonTruckingLiability?: {
    status?: CoverageStatus;
    limit: string;
  };
  hiredAutoLiability?: {
    status?: CoverageStatus;
    combinedSingleLimit: string;
  };
  hiredNonOwnedLiability?: {
    status?: CoverageStatus;
    limit: string;
  };
  umbrella?: {
    status?: CoverageStatus;
    eachOccurrence: string;
    aggregate: string;
    isExcess: boolean;
    deductible: string;
  };
  workersComp?: {
    status?: CoverageStatus;
    perStatute: boolean;
    elEachAccident: string;
    elDiseaseEaEmployee: string;
    elDiseasePolicyLimit: string;
  };
  cargo?: {
    status?: CoverageStatus;
    limit: string;
    deductible: string;
    // CSR-1566: when set, the COI renders the label as
    // "Motor Truck Cargo/Reefer Breakdown" (same limit/deductible, one row).
    reeferBreakdown?: boolean;
  };
  onHook?: {
    status?: CoverageStatus;
    limit: string;
    deductible: string;
  };
  physicalDamage?: {
    status?: CoverageStatus;
    comprehensiveDeductible: string;
    collisionDeductible: string;
  };
  trailerInterchange?: {
    status?: CoverageStatus;
    limit: string;
    deductible: string;
  };
  nonOwnedTrailerPhysicalDamage?: {
    status?: CoverageStatus;
    limit: string;
    deductible: string;
  };
  rentalReimbursement?: {
    status?: CoverageStatus;
    perDay: string;
    maxPayout: string;
    appliesTo: string;
  };
  nonOwnedAuto?: {
    status?: CoverageStatus;
    limit: string;
  };
}

// US States for dropdowns
export const US_STATES = [
  { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" }, { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" }, { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" }, { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" }, { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" }, { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" }, { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" }, { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" }, { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" }, { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" }, { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" }, { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" }, { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" }, { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" }, { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" }, { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" }, { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" }, { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" }, { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" }, { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" }, { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" }, { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" }, { value: "WY", label: "Wyoming" },
] as const;

// Insert schemas
export const insertAgencySchema = createInsertSchema(agencies).omit({
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
});

export const insertAgentSchema = createInsertSchema(agents).omit({
  createdAt: true,
  updatedAt: true,
});

// CSR-1527 — the policy/insured Zod schemas are STANDALONE `z.object`s (no
// `createInsertSchema(policies|insureds)` / `createSelectSchema(insureds)`), so
// the `policies`/`insureds` pgTables could be dropped (PR-B2) without breaking
// them. Field types and optional/nullable exactly mirror drizzle-zod 0.7.1's
// output for the former tables (int → int32-bounded, decimal → string, jsonb →
// drizzle-zod's own table-independent `jsonSchema`, timestamp → date). PR-A
// proved this type-identical via temporary `$inferSelect` assertions (removed
// with the pgTables in PR-B2).
//
// pg `integer` — drizzle-zod bounds it to the int32 range and marks it .int().
const pgInt4 = () => z.number().int().gte(-2147483648).lte(2147483647);

// Insertable policy columns shared by insertPolicySchema and
// insertManualPolicySchema. Both already omit syncedAt/createdAt/updatedAt
// (defaulted) and physicalDamageVehicles (AMS-only, written only via
// storage.setPhysicalDamageVehicles — never a validated write path), so those
// are simply absent here. Manual additionally omits id/externalId and overrides
// the money/commission fields (see insertManualPolicySchema below).
const policyInsertColumns = {
  id: z.string(),
  externalId: pgInt4().nullable().optional(),
  policyNumber: z.string(),
  customerName: z.string(),
  carrier: z.string(),
  parentCarrier: z.string().nullable().optional(),
  status: z.string().optional(),
  substatus: z.enum(POLICY_SUBSTATUSES).nullable().optional(),
  producer: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  effectiveDate: z.string(),
  expirationDate: z.string(),
  premium: z.string().nullable().optional(),
  policyPremium: z.string().nullable().optional(),
  brokerFee: z.string().nullable().optional(),
  policyFees: z.string().nullable().optional(),
  taxes: z.string().nullable().optional(),
  commissionPct: z.string().nullable().optional(),
  dotNumber: z.string().nullable().optional(),
  agency: z.string().nullable().optional(),
  agencyId: z.string().nullable().optional(),
  insuredId: z.string().nullable().optional(),
  companyEmail: z.string().nullable().optional(),
  carrierNaic: z.string().nullable().optional(),
  vehicles: jsonSchema.nullable().optional(),
  trailers: jsonSchema.nullable().optional(),
  drivers: jsonSchema.nullable().optional(),
  coverages: jsonSchema.nullable().optional(),
  additionalInsureds: jsonSchema.nullable().optional(),
  waiversOfSubrogation: jsonSchema.nullable().optional(),
  lossPayees: jsonSchema.nullable().optional(),
  blanketWaiverOfSubrogation: z.boolean().nullable().optional(),
  mga: z.string().nullable().optional(),
  financeCompany: z.string().nullable().optional(),
  paymentPlan: z.string().nullable().optional(),
  boundDate: z.string().nullable().optional(),
  cancellationDate: z.string().nullable().optional(),
  dbaName: z.string().nullable().optional(),
  carrierScrapedAt: z.date().nullable().optional(),
  lastSeenInDc: z.date().nullable().optional(),
  deletedAt: z.date().nullable().optional(),
};

export const insertPolicySchema = z.object(policyInsertColumns);

// Insertable insured columns (createInsertSchema(insureds) minus the defaulted
// syncedAt/createdAt/updatedAt). `name` keeps the CSR-1151 (S-001) 255-char cap.
const insuredInsertColumns = {
  id: z.string(),
  externalId: pgInt4().nullable().optional(),
  // NOTE (CSR-1527 PR-A): `name` is `.nullable()` WITHOUT `.optional()` — this
  // matches drizzle-zod's inferred type for an overridden column (the `name`
  // 255-cap override makes the key required-nullable in the type, even though
  // its runtime schema is also optional). Every consumer applies `.partial()`
  // (routes PATCH + the CSR-1151 test), so the base optionality is moot at
  // runtime; the required-nullable form keeps the exported `InsertInsured` type
  // byte-identical to the former drizzle-zod shape (CSR-1527 PR-A).
  name: z.string().max(255, "Name must be 255 characters or fewer").nullable(),
  commercialName: z.string().nullable().optional(),
  dbaName: z.string().nullable().optional(),
  dotNumber: z.string().nullable().optional(),
  mcNumber: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  telephone: z.string().nullable().optional(),
  fax: z.string().nullable().optional(),
  phyStreet: z.string().nullable().optional(),
  phyCity: z.string().nullable().optional(),
  phyState: z.string().nullable().optional(),
  phyZip: z.string().nullable().optional(),
  phyCountry: z.string().nullable().optional(),
  mailingStreet: z.string().nullable().optional(),
  mailingCity: z.string().nullable().optional(),
  mailingState: z.string().nullable().optional(),
  mailingZip: z.string().nullable().optional(),
  mailingCountry: z.string().nullable().optional(),
  businessOrg: z.string().nullable().optional(),
  companyOfficer: z.string().nullable().optional(),
  radius: z.string().nullable().optional(),
  fmcsaStatusCode: z.string().nullable().optional(),
  totalDrivers: pgInt4().nullable().optional(),
  totalPowerUnits: pgInt4().nullable().optional(),
  totalFleetSize: z.string().nullable().optional(),
  sources: z.string().nullable().optional(),
  salesAgent: z.string().nullable().optional(),
  retentionAgent: z.string().nullable().optional(),
  inspectionSummary: jsonSchema.nullable().optional(),
  crashSummary: jsonSchema.nullable().optional(),
  violations: jsonSchema.nullable().optional(),
  basicScores: jsonSchema.nullable().optional(),
  contacts: jsonSchema.nullable().optional(),
  nowcertsDatabaseId: z.string().nullable().optional(),
  carrierScrapedAt: z.date().nullable().optional(),
  deletedAt: z.date().nullable().optional(),
};

export const insertInsuredSchema = z.object(insuredInsertColumns);

// Full insured row (createSelectSchema(insureds)): every column present,
// nullable (not optional); notNull columns required. `name` has NO 255 cap here
// (the select schema never received the insert-side override).
const insuredSelectColumns = {
  id: z.string(),
  externalId: pgInt4().nullable(),
  name: z.string().nullable(),
  commercialName: z.string().nullable(),
  dbaName: z.string().nullable(),
  dotNumber: z.string().nullable(),
  mcNumber: z.string().nullable(),
  email: z.string().nullable(),
  telephone: z.string().nullable(),
  fax: z.string().nullable(),
  phyStreet: z.string().nullable(),
  phyCity: z.string().nullable(),
  phyState: z.string().nullable(),
  phyZip: z.string().nullable(),
  phyCountry: z.string().nullable(),
  mailingStreet: z.string().nullable(),
  mailingCity: z.string().nullable(),
  mailingState: z.string().nullable(),
  mailingZip: z.string().nullable(),
  mailingCountry: z.string().nullable(),
  businessOrg: z.string().nullable(),
  companyOfficer: z.string().nullable(),
  radius: z.string().nullable(),
  fmcsaStatusCode: z.string().nullable(),
  totalDrivers: pgInt4().nullable(),
  totalPowerUnits: pgInt4().nullable(),
  totalFleetSize: z.string().nullable(),
  sources: z.string().nullable(),
  salesAgent: z.string().nullable(),
  retentionAgent: z.string().nullable(),
  inspectionSummary: jsonSchema.nullable(),
  crashSummary: jsonSchema.nullable(),
  violations: jsonSchema.nullable(),
  basicScores: jsonSchema.nullable(),
  contacts: jsonSchema.nullable(),
  nowcertsDatabaseId: z.string().nullable(),
  syncedAt: z.date(),
  carrierScrapedAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
};

export const selectInsuredSchema = z.object(insuredSelectColumns);

// CSR-1604 — premium breakdown components are decimal money strings. Validate
// the shape at the boundary so bad input (e.g. "abc") is a 400, not a silent
// 0 in the Total followed by a numeric-column 500. Up to 8 integer digits
// (numeric(10,2)) + optional ≤2 decimals. Empty string is normalized to null
// (blank) so it never reaches the numeric column as "".
const moneyStringField = z.preprocess(
  (value) => (value === "" ? null : value),
  z
    .string()
    .regex(MONEY_STRING_PATTERN, "must be a dollar amount")
    .nullable()
    .optional(),
);

// CSR-1606 — commission percent as a decimal string, bounded 0–100. Shared by
// the manual-policy schemas and the carrier commission-rate insert below so
// the range rule can't drift between the two write paths.
const isCommissionPctInRange = (value: string) => {
  const num = Number(value);
  return Number.isFinite(num) && num >= 0 && num <= 100;
};
const COMMISSION_PCT_RANGE_MESSAGE = "must be between 0 and 100";

export const commissionPctStringField = z.preprocess(
  (value) => (value === "" ? null : value),
  z
    .string()
    .refine(isCommissionPctInRange, COMMISSION_PCT_RANGE_MESSAGE)
    .nullable()
    .optional(),
);

// CSR-2000 — effective/expiration were plain z.string(), so packed ("08222026"),
// impossible ("2027-29-01"), and placeholder ("MM/DD/YYYY") values were stored and
// then silently dropped from every date-driven view.
const policyDateField = z.string().refine(isValidPolicyDate, POLICY_DATE_FORMAT_MESSAGE);
const optionalPolicyDateField = z.preprocess(
  (value) => (value === "" ? null : value),
  z.string().refine(isValidPolicyDate, POLICY_DATE_FORMAT_MESSAGE).nullable().optional(),
);

// CSR-1527 PR-A — standalone (see policyInsertColumns above). Same as
// insertPolicySchema minus id/externalId (server-assigned), with the money
// fields (policyPremium/brokerFee/policyFees/taxes) and commissionPct overridden
// to their validated string fields. physicalDamageVehicles is already absent
// from policyInsertColumns (AMS-only; written only via setPhysicalDamageVehicles).
export const insertManualPolicySchema = z
  .object(policyInsertColumns)
  .omit({
    id: true,
    externalId: true,
  })
  .extend({
    effectiveDate: policyDateField,
    expirationDate: optionalPolicyDateField,
    boundDate: optionalPolicyDateField,
    cancellationDate: optionalPolicyDateField,
    policyPremium: moneyStringField,
    brokerFee: moneyStringField,
    policyFees: moneyStringField,
    taxes: moneyStringField,
    commissionPct: commissionPctStringField,
  });

export const insertSyncMetadataSchema = createInsertSchema(syncMetadata);

export const insertPolicyDocumentSchema = createInsertSchema(policyDocuments).omit({
  createdAt: true,
});

// CSR-2056 — `acordFormId` is validated against the CSR-2055 registry rather than
// accepted as free text, so a document can never be filed naming a form that does
// not exist. Derived from ACORD_FORMS itself, so adding a form to the registry
// widens this automatically and the two cannot drift.
const ACORD_FORM_ID_VALUES = ACORD_FORMS.map((form) => form.id) as [
  AcordFormId,
  ...AcordFormId[],
];

export const insertInsuredDocumentSchema = createInsertSchema(insuredDocuments)
  .omit({
    createdAt: true,
  })
  .extend({
    acordFormId: z.enum(ACORD_FORM_ID_VALUES).nullish(),
  });

export const insertActivityLogSchema = createInsertSchema(activityLogs).omit({
  createdAt: true,
});

// CSR-2197 (PB7.3) — insert/updateCertificateHolderSchema moved to
// ./coi-service-types (re-exported below): they gate the create/PATCH relay
// routes, and could no longer be derived from a dropped table.

export const insertCarrierSchema = createInsertSchema(carriers).omit({
  createdAt: true,
});

// CSR-588 — Insurance Companies. Route layer (CSR-818) is responsible for:
//   * rejecting commission fields when category = parent_company,
//   * rejecting parent_company_id when category = parent_company,
//   * validating that a supplied parent_company_id resolves to a row whose
//     own category is parent_company.
// This schema only enforces shape + types.
export const insertInsuranceCompanySchema = createInsertSchema(
  insuranceCompanies,
).omit({
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
});

export const updateInsuranceCompanySchema = insertInsuranceCompanySchema
  .omit({ id: true })
  .partial();

export const insertInsuranceCompanyContactSchema = createInsertSchema(
  insuranceCompanyContacts,
).omit({
  createdAt: true,
  updatedAt: true,
});

export const updateInsuranceCompanyContactSchema =
  insertInsuranceCompanyContactSchema
    .omit({ id: true, insuranceCompanyId: true })
    .partial();

// CSR-1606 — carrier commission override rows. `ratePct` reuses the shared
// 0–100 bound; `lineOfBusiness` must be one of the dropdown values, enforced
// at the route layer against the live policy-type list.
export const insertInsuranceCompanyCommissionRateSchema = createInsertSchema(
  insuranceCompanyCommissionRates,
)
  .omit({
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    lineOfBusiness: z.string().trim().min(1, "lineOfBusiness is required"),
    ratePct: z
      .string()
      .refine(isCommissionPctInRange, COMMISSION_PCT_RANGE_MESSAGE),
  });




export const insertPolicyChangeHistorySchema = createInsertSchema(policyChangeHistory).omit({
  changedAt: true,
});

export const insertInsuredChangeHistorySchema = createInsertSchema(insuredChangeHistory).omit({
  changedAt: true,
});
export const selectInsuredChangeHistorySchema = createSelectSchema(insuredChangeHistory);

// CSR-771 — both createdAt and updatedAt default to NOW() at the DB layer.
export const insertNoteSchema = createInsertSchema(notes).omit({
  createdAt: true,
  updatedAt: true,
});
export const selectNoteSchema = createSelectSchema(notes);

export const insertInsuredCoiNotesSchema = createInsertSchema(insuredCoiNotes).omit({
  updatedAt: true,
});
export const selectInsuredCoiNotesSchema = createSelectSchema(insuredCoiNotes);

// CSR-1117 — savedAt/updatedAt default at the DB layer; id is generated server-side.
export const insertInsuredCoiTemplateSchema = createInsertSchema(insuredCoiTemplates).omit({
  id: true,
  savedAt: true,
  updatedAt: true,
});
export const selectInsuredCoiTemplateSchema = createSelectSchema(insuredCoiTemplates);

// CSR-1627 — updatedAt defaults at the DB layer; id is generated server-side.
export const insertInsuredCoiSpecialVerbiageSchema = createInsertSchema(insuredCoiSpecialVerbiage).omit({
  id: true,
  updatedAt: true,
});
export const selectInsuredCoiSpecialVerbiageSchema = createSelectSchema(insuredCoiSpecialVerbiage);

export const selectCarrierSchema = createSelectSchema(carriers);

export const insertSystemSettingsSchema = createInsertSchema(systemSettings).omit({
  updatedAt: true,
});
export const selectSystemSettingsSchema = createSelectSchema(systemSettings);


// Feature flag registry — admin CRUD payloads.
// `id`/timestamps are server-generated; clients send name/isActive/description.
export const insertFeatureFlagSchema = createInsertSchema(featureFlags, {
  name: z.string().trim().min(1, "Name is required").max(255),
  description: z.string().trim().max(1000).optional().nullable(),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// PATCH payload — every field optional, but at least one must be present.
// Server-controlled timestamps and `id` are intentionally excluded.
export const updateFeatureFlagSchema = z
  .object({
    name: z.string().trim().min(1).max(255).optional(),
    isActive: z.boolean().optional(),
    description: z.string().trim().max(1000).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, {
    message: "At least one field must be provided",
  });

export const selectFeatureFlagSchema = createSelectSchema(featureFlags);

// Select schemas
export const selectAgencySchema = createSelectSchema(agencies);
export const selectAgentSchema = createSelectSchema(agents);
export const selectPolicyDocumentSchema = createSelectSchema(policyDocuments);
export const selectInsuredDocumentSchema = createSelectSchema(insuredDocuments);
export const selectActivityLogSchema = createSelectSchema(activityLogs);
export const selectSyncMetadataSchema = createSelectSchema(syncMetadata);
export const selectPolicyChangeHistorySchema = createSelectSchema(policyChangeHistory);

// Types
export type InsertAgency = z.infer<typeof insertAgencySchema>;
export type Agency = typeof agencies.$inferSelect;

export type UpsertUser = typeof users.$inferInsert;
export type User = typeof users.$inferSelect;

export type InsertAgent = z.infer<typeof insertAgentSchema>;
export type Agent = typeof agents.$inferSelect;

export type InsertPolicy = z.infer<typeof insertPolicySchema>;
export type InsertManualPolicy = z.infer<typeof insertManualPolicySchema>;

// CSR-1901 (CSR-1672) — normalized cancellation-reason classification, mirroring
// the DC `cancellation_reason` enum (DC #530 / CSR-1897). AMS only reads these
// values from the DC External API; they are never written here.
// CSR-2264 — `insured_request` is the fourth DC literal. Every literal here needs
// a client `CANCELLATION_REASON_LABELS` entry or `tsc` fails.
export const CANCELLATION_REASONS = [
  "non_pay",
  "underwriting",
  "insured_request",
  "other",
] as const;
export type CancellationReason = (typeof CANCELLATION_REASONS)[number];

// CSR-1527 — standalone select shape (was `typeof policies.$inferSelect`), now
// the sole definition after the `policies` pgTable was dropped (PR-B2). Every
// column carries its exact former $inferSelect type (jsonb → `unknown`).
export type Policy = {
  id: string;
  externalId: number | null;
  policyNumber: string;
  customerName: string;
  carrier: string;
  parentCarrier: string | null;
  status: string;
  // CSR-2336 — qualifies `status` without replacing it (null = none). Optional
  // because a DC deployment that predates CSR-2350 never sends it.
  substatus?: PolicySubstatus | null;
  producer: string | null;
  type: string | null;
  // CSR-1768 — nullable: a DC row with an unrecoverable/placeholder date (e.g.
  // the literal "MM/DD/YYYY") maps to null instead of an invented today, so AMS
  // renders "—" rather than a fabricated date.
  effectiveDate: string | null;
  expirationDate: string | null;
  premium: string | null;
  policyPremium: string | null;
  brokerFee: string | null;
  policyFees: string | null;
  taxes: string | null;
  commissionPct: string | null;
  dotNumber: string | null;
  agency: string | null;
  agencyId: string | null;
  insuredId: string | null;
  companyEmail: string | null;
  carrierNaic: string | null;
  vehicles: unknown;
  trailers: unknown;
  drivers: unknown;
  coverages: unknown;
  additionalInsureds: unknown;
  waiversOfSubrogation: unknown;
  lossPayees: unknown;
  blanketWaiverOfSubrogation: boolean | null;
  physicalDamageVehicles: unknown;
  mga: string | null;
  financeCompany: string | null;
  paymentPlan: string | null;
  boundDate: string | null;
  cancellationDate: string | null;
  // CSR-1901 (CSR-1672) — structured cancellation enrichment read from the DC
  // External API (columns added by CSR-1897; populated by the CSR-1898/1899
  // extractors). `cancellationReason` classifies the cancellation; `amountDue`
  // is the pay-to-keep figure, non-null only when reason === "non_pay".
  cancellationReason: CancellationReason | null;
  // Generic carrier status banner (CSR-975) or exact cancellation wording.
  // Optional because only policy reads expose it; mutation echoes retain their contract.
  statusReason?: string | null;
  // CSR-2326 — the DC AOR flag (CSR-2323): when the customer was found to have
  // moved agency (null = not flagged), who raised it, and the other agency's
  // code when known. Read-only enrichment, like `statusReason`.
  aorIdentifiedAt?: string | null;
  aorSource?: AorSource | null;
  carrierAgencyCode?: string | null;
  amountDue: string | null;
  dbaName: string | null;
  syncedAt: Date;
  carrierScrapedAt: Date | null;
  lastSeenInDc: Date | null;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
};

export type InsertInsured = z.infer<typeof insertInsuredSchema>;
// CSR-1527 — standalone select shape (was `typeof insureds.$inferSelect`), now
// the sole definition after the `insureds` pgTable was dropped (PR-B2).
export type Insured = {
  id: string;
  externalId: number | null;
  name: string | null;
  commercialName: string | null;
  dbaName: string | null;
  dotNumber: string | null;
  mcNumber: string | null;
  email: string | null;
  telephone: string | null;
  fax: string | null;
  phyStreet: string | null;
  phyCity: string | null;
  phyState: string | null;
  phyZip: string | null;
  phyCountry: string | null;
  mailingStreet: string | null;
  mailingCity: string | null;
  mailingState: string | null;
  mailingZip: string | null;
  mailingCountry: string | null;
  businessOrg: string | null;
  companyOfficer: string | null;
  radius: string | null;
  fmcsaStatusCode: string | null;
  totalDrivers: number | null;
  totalPowerUnits: number | null;
  totalFleetSize: string | null;
  sources: string | null;
  salesAgent: string | null;
  retentionAgent: string | null;
  inspectionSummary: unknown;
  crashSummary: unknown;
  violations: unknown;
  basicScores: unknown;
  contacts: unknown;
  nowcertsDatabaseId: string | null;
  syncedAt: Date;
  carrierScrapedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
};

export type InsertActivityLog = z.infer<typeof insertActivityLogSchema>;
export type ActivityLog = typeof activityLogs.$inferSelect;

export type InsertPolicyDocument = z.infer<typeof insertPolicyDocumentSchema>;
export type PolicyDocument = typeof policyDocuments.$inferSelect;

export type InsertInsuredDocument = z.infer<typeof insertInsuredDocumentSchema>;
export type InsuredDocument = typeof insuredDocuments.$inferSelect;

export type InsertSyncMetadata = z.infer<typeof insertSyncMetadataSchema>;
export type SyncMetadata = typeof syncMetadata.$inferSelect;

// CSR-2197 (PB7.3) — `CertificateHolder` / `InsertCertificateHolder` are
// re-exported below with the CSR-2196 history types: the table left this
// database, the shapes did not.

export type InsertCarrier = z.infer<typeof insertCarrierSchema>;
export type Carrier = typeof carriers.$inferSelect;

// CSR-588 — Insurance Companies + Contacts.
export type InsertInsuranceCompany = z.infer<typeof insertInsuranceCompanySchema>;
export type UpdateInsuranceCompany = z.infer<typeof updateInsuranceCompanySchema>;
export type InsuranceCompany = typeof insuranceCompanies.$inferSelect;
export type InsuranceCompanyCategory = InsuranceCompany["category"];

export const INSURANCE_COMPANY_CATEGORIES = [
  "parent_company",
  "insurance_carrier",
  "mga",
] as const satisfies readonly InsuranceCompanyCategory[];

export type InsertInsuranceCompanyContact = z.infer<
  typeof insertInsuranceCompanyContactSchema
>;
export type UpdateInsuranceCompanyContact = z.infer<
  typeof updateInsuranceCompanyContactSchema
>;
export type InsuranceCompanyContact =
  typeof insuranceCompanyContacts.$inferSelect;

export type InsertInsuranceCompanyCommissionRate = z.infer<
  typeof insertInsuranceCompanyCommissionRateSchema
>;
export type InsuranceCompanyCommissionRate =
  typeof insuranceCompanyCommissionRates.$inferSelect;

// CSR-2196 (PB7.2) — these four stopped being tables in this database. The COI
// service owns that history and AMS relays it, so the shapes are hand-written in
// ./coi-service-types and re-exported here to keep `@shared/schema` the single
// import path consumers already use. The `Insert*` counterparts are gone with
// the tables: nothing in AMS writes a COI history row any more.
export type {
  COIIssuance,
  COIRequest,
  InboundEmail,
  IntercomEvent,
  // CSR-2197 (PB7.3): the certificate-holder shapes joined them when their
  // table left. The AI-link type had no consumers and is gone outright.
  CertificateHolder,
  InsertCertificateHolder,
} from "./coi-service-types";

// Value exports (zod validators), unlike the type-only block above: they gate
// the create/PATCH relay routes for certificate holders (CSR-745 S-001) and
// could no longer be derived from a dropped table.
export {
  insertCertificateHolderSchema,
  updateCertificateHolderSchema,
} from "./coi-service-types";

// CSR-1602: provenance marker stashed by CSR-1582 on the Intercom event when
// it recovers a forwarded COI email body. Lives under
// `intercom_events.eventData._logrock.forwardedRecovery`. Read defensively —
// absent for non-recovered requests, and a malformed marker must fail-soft to
// null, so the server validates it through this schema before returning it.
export const forwardedRecoveryMetaSchema = z.object({
  messageId: z.string(),
  recoveredAt: z.string(),
  // "gmail" is the CSR-1581 Tier-2 source (body recovered from the
  // support@logrock.com mailbox by rfc822msgid); "intercom_history" is the
  // CSR-1582 Tier-1 source. Backward compatible — existing markers validate.
  source: z.union([z.literal("intercom_history"), z.literal("gmail")]),
});
export type ForwardedRecoveryMeta = z.infer<typeof forwardedRecoveryMetaSchema>;

// COIRequest is re-exported from ./coi-service-types above. CoiExclusionPhrase
// is gone outright — the admin console that curated those phrases left with
// CSR-2182 and no consumer named the type.

export type InsertFeatureFlag = z.infer<typeof insertFeatureFlagSchema>;
export type UpdateFeatureFlag = z.infer<typeof updateFeatureFlagSchema>;
export type FeatureFlag = typeof featureFlags.$inferSelect;

export type InsertPolicyChangeHistory = z.infer<typeof insertPolicyChangeHistorySchema>;
export type PolicyChangeHistory = typeof policyChangeHistory.$inferSelect;

export type InsertInsuredChangeHistory = z.infer<typeof insertInsuredChangeHistorySchema>;
export type InsuredChangeHistory = typeof insuredChangeHistory.$inferSelect;

export type InsertNote = z.infer<typeof insertNoteSchema>;
export type Note = typeof notes.$inferSelect;

// CSR-771 — convenience type used by the route layer + client hooks.
// `policyNumber` is left-joined from `policies` for AMS-policy and NowCerts notes.
// `authorDisplayNameResolved` is the server-resolved fallback chain
// (firstName + " " + lastName → firstName → email → snapshot → "Unknown").
export type NoteWithJoinedFields = Note & {
  policyNumber: string | null;
  authorDisplayNameResolved: string;
};

export type InsertInsuredCoiNotes = z.infer<typeof insertInsuredCoiNotesSchema>;
export type InsuredCoiNotes = typeof insuredCoiNotes.$inferSelect;

export type InsertInsuredCoiTemplate = z.infer<typeof insertInsuredCoiTemplateSchema>;
export type InsuredCoiTemplate = typeof insuredCoiTemplates.$inferSelect;

export type InsertInsuredCoiSpecialVerbiage = z.infer<typeof insertInsuredCoiSpecialVerbiageSchema>;
export type InsuredCoiSpecialVerbiage = typeof insuredCoiSpecialVerbiage.$inferSelect;

// CSR-792 — Loss Payees: row + link table types.
export type LossPayee = typeof lossPayees.$inferSelect;
export type InsertLossPayee = typeof lossPayees.$inferInsert;
export type LossPayeeCoverage = typeof lossPayeeCoverages.$inferSelect;
export type InsertLossPayeeCoverage = typeof lossPayeeCoverages.$inferInsert;
export type LossPayeeVehicle = typeof lossPayeeVehicles.$inferSelect;
export type InsertLossPayeeVehicle = typeof lossPayeeVehicles.$inferInsert;

// CSR-1101 — Waivers of Subrogation: row + link table types.
export type WaiverOfSubrogation = typeof waiversOfSubrogation.$inferSelect;
export type InsertWaiverOfSubrogation = typeof waiversOfSubrogation.$inferInsert;
export type WaiverOfSubrogationCoverage = typeof waiverOfSubrogationCoverages.$inferSelect;
export type InsertWaiverOfSubrogationCoverage = typeof waiverOfSubrogationCoverages.$inferInsert;

// CSR-1658 — Commodities: row + link table types + the read shape returned by
// getInsuredCommodities (shared by storage and the client hook).
export type Commodity = typeof commodities.$inferSelect;
export type InsertCommodity = typeof commodities.$inferInsert;
export type InsuredCommodity = typeof insuredCommodities.$inferSelect;
export type InsertInsuredCommodity = typeof insuredCommodities.$inferInsert;
export interface InsuredCommodityLink {
  id: string;
  commodityId: string;
  name: string;
  createdAt: string;
}

export type SystemSettings = typeof systemSettings.$inferSelect;
export type InsertSystemSettings = z.infer<typeof insertSystemSettingsSchema>;
